import React, { useState, useEffect } from 'react';
import { 
  Settings, Plus, Trash2, Edit2, Save, X, 
  GripVertical, Check, AlertTriangle, RefreshCw,
  Palette, Hash, Database, Loader2, Mail, FileText,
  ArrowUp, ArrowDown
} from 'lucide-react';
import api, { statutsProgressionAPI } from '../services/api';
import EmailAccountsConfig from '../components/config/EmailAccountsConfig';

const ConfigurationAdmin = () => {
  const [activeTab, setActiveTab] = useState('statuts');
  const [statuts, setStatuts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingStatut, setEditingStatut] = useState(null);
  const [saving, setSaving] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [migrationResult, setMigrationResult] = useState(null);
  
  const [formData, setFormData] = useState({
    code: '',
    label: '',
    couleur: 'bg-gray-500',
    progress: 0
  });

  const COLOR_OPTIONS = [
    { value: 'bg-blue-500', label: 'Bleu', hex: '#3b82f6' },
    { value: 'bg-violet-500', label: 'Violet', hex: '#8b5cf6' },
    { value: 'bg-orange-500', label: 'Orange', hex: '#f97316' },
    { value: 'bg-cyan-500', label: 'Cyan', hex: '#06b6d4' },
    { value: 'bg-yellow-500', label: 'Jaune', hex: '#eab308' },
    { value: 'bg-gray-500', label: 'Gris', hex: '#6b7280' },
    { value: 'bg-red-500', label: 'Rouge', hex: '#ef4444' },
    { value: 'bg-green-500', label: 'Vert', hex: '#22c55e' },
    { value: 'bg-emerald-600', label: 'Émeraude', hex: '#059669' },
    { value: 'bg-purple-500', label: 'Pourpre', hex: '#a855f7' },
  ];

  useEffect(() => {
    loadStatuts();
  }, []);

  const loadStatuts = async () => {
    try {
      const res = await statutsProgressionAPI.getAllWithInactive();
      setStatuts(res.data || []);
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMigration = async () => {
    if (!window.confirm('Voulez-vous lancer la migration des dossiers ? Cette opération va ajouter les nouveaux champs aux anciens dossiers.')) {
      return;
    }
    
    setMigrating(true);
    setMigrationResult(null);
    try {
      const res = await api.post('/config/migrate-dossiers');
      setMigrationResult({
        success: true,
        message: res.data.message,
        details: res.data.details
      });
    } catch (error) {
      console.error('Erreur migration:', error);
      setMigrationResult({
        success: false,
        message: error.response?.data?.detail || 'Erreur lors de la migration'
      });
    } finally {
      setMigrating(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.code || !formData.label) {
      alert('Le code et le label sont obligatoires');
      return;
    }
    
    setSaving(true);
    try {
      await statutsProgressionAPI.create({
        code: formData.code.toLowerCase().replace(/\s+/g, '_'),
        label: formData.label,
        couleur: formData.couleur,
        progress: formData.progress || 0,
        ordre: statuts.length + 1
      });
      await loadStatuts();
      setShowCreateModal(false);
      resetForm();
    } catch (error) {
      console.error('Erreur:', error);
      alert(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    setSaving(true);
    try {
      await statutsProgressionAPI.update(editingStatut.id, {
        label: formData.label,
        couleur: formData.couleur,
        progress: formData.progress
      });
      await loadStatuts();
      setEditingStatut(null);
      resetForm();
    } catch (error) {
      console.error('Erreur:', error);
      alert(error.response?.data?.detail || 'Erreur lors de la modification');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (statut) => {
    if (!window.confirm(`Supprimer le statut "${statut.label}" ?`)) return;
    
    try {
      await statutsProgressionAPI.delete(statut.id);
      await loadStatuts();
    } catch (error) {
      console.error('Erreur:', error);
      alert(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const moveStatut = async (index, direction) => {
    const newStatuts = [...statuts];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= newStatuts.length) return;
    
    [newStatuts[index], newStatuts[targetIndex]] = [newStatuts[targetIndex], newStatuts[index]];
    
    try {
      await statutsProgressionAPI.reorder(newStatuts.map(s => s.id));
      await loadStatuts();
    } catch (error) {
      console.error('Erreur réordonnancement:', error);
    }
  };

  const handleToggle = async (statut) => {
    try {
      await statutsProgressionAPI.update(statut.id, { actif: !statut.actif });
      await loadStatuts();
    } catch (error) {
      console.error('Erreur:', error);
      alert(error.response?.data?.detail || 'Erreur');
    }
  };

  const openEditModal = (statut) => {
    setFormData({
      code: statut.code,
      label: statut.label,
      couleur: statut.couleur,
      progress: statut.progress || 0
    });
    setEditingStatut(statut);
  };

  const resetForm = () => {
    setFormData({
      code: '',
      label: '',
      couleur: 'bg-gray-500',
      progress: 0
    });
  };

  const getColorHex = (colorClass) => {
    const option = COLOR_OPTIONS.find(c => c.value === colorClass);
    return option?.hex || '#6b7280';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">⚙️ Configuration</h1>
          <p className="text-gray-600 mt-1">Gérez les paramètres de l'application</p>
        </div>
        <button
          onClick={loadStatuts}
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition"
        >
          <RefreshCw className="w-4 h-4" />
          Actualiser
        </button>
      </div>

      {/* Onglets */}
      <div className="flex gap-2 border-b border-gray-200 pb-2">
        <button
          onClick={() => setActiveTab('statuts')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition ${
            activeTab === 'statuts'
              ? 'bg-blue-100 text-blue-700'
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <FileText className="w-4 h-4" />
          Statuts & Migration
        </button>
        <button
          onClick={() => setActiveTab('email')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition ${
            activeTab === 'email'
              ? 'bg-indigo-100 text-indigo-700'
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <Mail className="w-4 h-4" />
          Comptes Email
        </button>
      </div>

      {/* Contenu des onglets */}
      {activeTab === 'email' ? (
        <EmailAccountsConfig />
      ) : (
        <>
          {/* Section Migration Base de données */}
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl shadow-sm border border-amber-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-amber-200 bg-amber-100/50">
              <h2 className="text-lg font-bold text-amber-900 flex items-center gap-2">
                <Database className="w-5 h-5" />
                🔧 Migration des dossiers
              </h2>
              <p className="text-sm text-amber-700 mt-1">
                Utilisez cette fonction après un déploiement pour mettre à jour les anciens dossiers avec les nouveaux champs
              </p>
            </div>
            <div className="p-6">
              <div className="flex items-center gap-4">
                <button
                  onClick={handleMigration}
                  disabled={migrating}
                  className="flex items-center gap-2 px-6 py-3 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition disabled:opacity-50 font-semibold"
                >
                  {migrating ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Migration en cours...
                    </>
                  ) : (
                    <>
                      <Database className="w-5 h-5" />
                      Lancer la migration
                    </>
                  )}
                </button>
                
                {migrationResult && (
                  <div className={`flex-1 p-4 rounded-lg ${migrationResult.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    <p className="font-medium">{migrationResult.success ? '✅' : '❌'} {migrationResult.message}</p>
                    {migrationResult.details && migrationResult.details.length > 0 && (
                      <ul className="mt-2 text-sm space-y-1">
                        {migrationResult.details.map((d, i) => (
                          <li key={i}>• {d.reference}: {d.fields_added.join(', ')}</li>
                        ))}
                      </ul>
                )}
              </div>
            )}
          </div>
          
          <div className="mt-4 p-4 bg-white/50 rounded-lg border border-amber-200">
            <p className="text-sm text-amber-800">
              <strong>Champs ajoutés par la migration :</strong><br/>
              • <code>is_viewed</code> - Marquage dossier consulté<br/>
              • <code>pipeline_statut</code> - Statut de progression<br/>
              • <code>aide_caisse</code> / <code>aide_departement</code> - Demandes d'aides
            </p>
          </div>
        </div>
      </div>

      {/* Section Statuts */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gray-50">
          <div>
            <h2 className="text-lg font-bold text-gray-900">📊 Gestion des statuts</h2>
            <p className="text-sm text-gray-600">Personnalisez les statuts des dossiers - ces statuts apparaissent automatiquement dans le menu et la barre de progression</p>
          </div>
          <button
            onClick={() => {
              resetForm();
              setShowCreateModal(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
          >
            <Plus className="w-4 h-4" />
            Nouveau statut
          </button>
        </div>

        <div className="p-6">
          <div className="space-y-3">
            {statuts.map((statut, index) => (
              <div 
                key={statut.id}
                className={`flex items-center gap-4 p-4 rounded-xl border-2 transition ${
                  statut.actif !== false
                    ? 'bg-white border-gray-200 hover:border-gray-300' 
                    : 'bg-gray-50 border-gray-100 opacity-60'
                }`}
              >
                {/* Ordre avec flèches */}
                <div className="flex flex-col items-center gap-1">
                  <button
                    onClick={() => moveStatut(index, 'up')}
                    disabled={index === 0}
                    className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <ArrowUp className="w-4 h-4" />
                  </button>
                  <span className="text-sm font-bold text-gray-500">{statut.ordre}</span>
                  <button
                    onClick={() => moveStatut(index, 'down')}
                    disabled={index === statuts.length - 1}
                    className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <ArrowDown className="w-4 h-4" />
                  </button>
                </div>
                
                {/* Couleur */}
                <div 
                  className="w-10 h-10 rounded-full shadow-sm flex-shrink-0"
                  style={{ backgroundColor: getColorHex(statut.couleur) }}
                />

                {/* Infos */}
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <h3 className="font-bold text-gray-900">{statut.label}</h3>
                    <code className="px-2 py-0.5 bg-gray-100 rounded text-xs text-gray-600">{statut.code}</code>
                    {statut.actif === false && (
                      <span className="px-2 py-0.5 bg-gray-200 text-gray-600 rounded-full text-xs font-medium">
                        Inactif
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                    <span className="flex items-center gap-1">
                      <div className="w-3 h-3 rounded" style={{ backgroundColor: getColorHex(statut.couleur) }}></div>
                      {statut.couleur}
                    </span>
                    <span>Progression: {statut.progress}%</span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => openEditModal(statut)}
                    className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition"
                    title="Modifier"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleToggle(statut)}
                    className={`px-3 py-1 rounded-lg text-xs font-medium transition ${
                      statut.actif !== false
                        ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                        : 'bg-green-100 text-green-700 hover:bg-green-200'
                    }`}
                  >
                    {statut.actif !== false ? 'Désactiver' : 'Activer'}
                  </button>
                  <button
                    onClick={() => handleDelete(statut)}
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                    title="Supprimer"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {statuts.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <Settings className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <p>Aucun statut configuré</p>
            </div>
          )}
        </div>
      </div>
        </>
      )}

      {/* Modal Création/Édition */}
      {(showCreateModal || editingStatut) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">
                {editingStatut ? '✏️ Modifier le statut' : '➕ Nouveau statut'}
              </h2>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setEditingStatut(null);
                  resetForm();
                }}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Code (non modifiable en édition) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Code technique *
                </label>
                <div className="relative">
                  <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })}
                    disabled={!!editingStatut}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 disabled:bg-gray-100"
                    placeholder="ex: test_eligibilite"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Lettres minuscules, chiffres et underscores uniquement</p>
              </div>

              {/* Label */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Label affiché *
                </label>
                <input
                  type="text"
                  value={formData.label}
                  onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  placeholder="ex: Test éligibilité"
                />
              </div>

              {/* Couleur */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Couleur
                </label>
                <div className="flex flex-wrap gap-2">
                  {COLOR_OPTIONS.map((color) => (
                    <button
                      key={color.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, couleur: color.value })}
                      className={`w-10 h-10 rounded-lg transition flex items-center justify-center ${
                        formData.couleur === color.value
                          ? 'ring-2 ring-offset-2 ring-gray-400'
                          : ''
                      }`}
                      style={{ backgroundColor: color.hex }}
                      title={color.label}
                    >
                      {formData.couleur === color.value && <Check className="w-5 h-5 text-white" />}
                    </button>
                  ))}
                </div>
              </div>

              {/* Progression */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Pourcentage de progression
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={formData.progress}
                    onChange={(e) => setFormData({ ...formData, progress: parseInt(e.target.value) })}
                    className="flex-1"
                  />
                  <span className="text-sm font-bold text-gray-700 w-12 text-right">{formData.progress}%</span>
                </div>
              </div>

              {/* Prévisualisation */}
              <div className="bg-gray-50 rounded-lg p-4">
                <label className="block text-xs font-medium text-gray-500 mb-2">PRÉVISUALISATION</label>
                <div className="flex items-center gap-3">
                  <div 
                    className="w-4 h-4 rounded-full"
                    style={{ backgroundColor: getColorHex(formData.couleur) }}
                  />
                  <div>
                    <span className="font-medium text-gray-900">{formData.label || 'Label'}</span>
                    <code className="ml-2 px-2 py-0.5 bg-gray-200 rounded text-xs text-gray-600">
                      {formData.code || 'code'}
                    </code>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setEditingStatut(null);
                  resetForm();
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg font-semibold text-gray-700 hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                onClick={editingStatut ? handleUpdate : handleCreate}
                disabled={saving || !formData.label || (!editingStatut && !formData.code)}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {saving ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <Save className="w-4 h-4" />
                )}
                {editingStatut ? 'Enregistrer' : 'Créer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConfigurationAdmin;
